import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { X, Sparkles, Clipboard } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useSettingsState } from "@/context/SettingsStateContext";
import useSettingsSubject from "@/hooks/useSettingsSubject";
import { useState } from "react";
import { parseBulkKeywords } from "@/utils/keywordUtils";
import ConceptsOverview from "@/components/settings/ConceptsOverview";

export default function KeywordsCard() {
  const { subjects, setSubjects } = useSettingsState();
  const { handleRemoveSubject } = useSettingsSubject();
  const [bulkInput, setBulkInput] = useState("");

  // Handle keyword addition
  const handleAddKeywords = () => {
    const newKeywords = parseBulkKeywords(bulkInput, subjects);
    if (newKeywords.length > 0) {
      setSubjects([...subjects, ...newKeywords]);
      setBulkInput("");
    }
  };

  return (
    <>
    <Card id="keywords">
      <CardHeader>
        <CardTitle>Keyword management</CardTitle>
        <CardDescription>
          Configure default keywords and tags for your content. You can add single keywords or paste multiple keywords from Excel.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Default Keywords</Label>
          <div className="flex flex-wrap gap-2">
            {subjects.map((subject, index) => (
              <Badge
                key={index}
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-200"
              >
                {subject}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 rounded-full p-0 ml-1 text-blue-700 hover:bg-blue-200 hover:text-blue-800"
                  onClick={() => handleRemoveSubject(subject)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
          </div>
          
          <div className="space-y-2 mt-2">
            <Textarea
              placeholder="Add keywords (single or multiple). You can paste from Excel - keywords will be separated by tabs, newlines, commas, or semicolons."
              value={bulkInput}
              onChange={(e) => setBulkInput(e.target.value)}
              className="min-h-[100px]"
            />
            <div className="flex gap-2">
              <Button onClick={handleAddKeywords} disabled={!bulkInput.trim()}>
                <Sparkles className="h-4 w-4 mr-1" />
                Add {parseBulkKeywords(bulkInput, subjects).length} Keywords
              </Button>
              <Button variant="outline" onClick={() => setBulkInput("")}>
                Clear
              </Button>
            </div>
            {bulkInput.trim() && (
              <p className="text-sm text-muted-foreground">
                {parseBulkKeywords(bulkInput, subjects).length} keywords will be added (duplicates will be skipped)
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
    
    {/* Concepts & Keywords Overview */}
    <div id="concepts" className="mt-6">
      <ConceptsOverview />
    </div>
    </>
  );
}
